import { ISelectElement } from "../../../../model/interface/keyValuePair";
import {
  ICreateTeacherQuestionRequest,
  ITeacherQuestionSearchRequest,
} from "../../../../api/crmContent.service";

/**
 * The number of parts a question may have. The editor's accordion
 * (handleAddPart) caps at 4, so this list must stay in sync with that cap.
 */
export const numberOfPartsOptions: Array<ISelectElement> = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
];

/** Parses a comma-separated passage-id string into positive ints. */
export const parsePassageIds = (input: string): Array<number> =>
  input
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map(Number)
    .filter((n) => !isNaN(n) && n > 0);

export interface IDuplicateQuestionSource {
  userId: number;
  cmsAuthorUserId?: number;
  districtId: number;
  stateId: number;
  standardId: number;
  dok: number;
  questionTypeId: number;
  passageIds: Array<number>;
}

/**
 * Builds the create payload for a duplicated question: settings are carried
 * over, contents are not. isPublished is reset to false and cmsAuthorNotes is
 * deliberately left unset, matching what QuestionNew does for a new question.
 */
export const buildDuplicateQuestionRequest = (
  source: IDuplicateQuestionSource
): ICreateTeacherQuestionRequest => ({
  userId: source.userId,
  cmsAuthorUserId: source.cmsAuthorUserId,
  districtId: source.districtId,
  stateId: source.stateId,
  standardId: source.standardId,
  dok: source.dok,
  questionTypeId: source.questionTypeId,
  passageIds: source.passageIds,
  isPublished: false,
});

/**
 * How the Question Bank searches: by the criteria filters, or by a single
 * Question ID. The two are exclusive on the screen (PLP-6413).
 */
export type QuestionSearchMode = "criteria" | "questionId";

/** Keeps digits only, the same rule as the Passage Bank's "Passage Id" field. */
export const sanitizeQuestionIdInput = (value: string): string =>
  value.replace(/[^0-9]/g, "");

/** The criteria filters, already converted from the screen's select values. */
export interface IQuestionSearchCriteria {
  subjectId?: number;
  domainId?: number;
  standardId?: number;
  dok?: number;
  isPublished?: boolean;
  questionTypeId?: number;
}

export interface IQuestionSearchInput {
  mode: QuestionSearchMode;
  questionIdText: string;
  criteria: IQuestionSearchCriteria;
  pageSize: number;
  pageNumber: number;
  orderByColumn: string;
  sortByDesc: boolean;
  districtId?: number;
}

/**
 * Builds the Question Bank list request for the current search mode, or
 * returns null when no request should be sent:
 * - the user's district has not loaded yet, so the search would not be
 *   district-scoped (the API rejects it with 400);
 * - Question ID mode with no ID entered.
 *
 * Question ID mode sends only the ID; Criteria mode never sends an ID.
 * Paging, sort and districtId are always included.
 */
export const buildQuestionSearchRequest = (
  input: IQuestionSearchInput
): ITeacherQuestionSearchRequest | null => {
  if (input.districtId === undefined) return null;

  const base: ITeacherQuestionSearchRequest = {
    pageSize: input.pageSize,
    pageNumber: input.pageNumber,
    orderByColumn: input.orderByColumn,
    sortByDesc: input.sortByDesc,
    districtId: input.districtId,
  };

  if (input.mode === "questionId") {
    const digits = sanitizeQuestionIdInput(input.questionIdText);
    if (digits === "") return null;
    return { ...base, teacherQuestionId: Number(digits) };
  }

  const { criteria } = input;
  return {
    ...base,
    subjectId: criteria.subjectId,
    domainId: criteria.domainId,
    standardId: criteria.standardId,
    dok: criteria.dok,
    isPublished: criteria.isPublished,
    questionTypeId: criteria.questionTypeId,
  };
};

/** The Learnosity item reference for a teacher (custom) question. */
export const getTeacherItemRef = (teacherQuestionId: number): string =>
  `${teacherQuestionId}-teacher`;

/**
 * Whether the editor's Preview Item link is usable. The preview loads the
 * saved Learnosity item, which does not exist until the question's first save,
 * and is held off while a save is in flight.
 */
export const canPreviewTeacherItem = (
  hasSavedItem: boolean,
  isSaving: boolean
): boolean => hasSavedItem && !isSaving;
